// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { createTheme } from '../design-system/theme';
import { useColorScheme } from '../foundations/colorScheme';
import { callHistoryStrings } from './strings';

export interface CallHistorySearchBarProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly placeholder?: string;
}

export const CallHistorySearchBar: React.FC<CallHistorySearchBarProps> = ({
  value,
  onChange,
  placeholder,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme);

  const handleClear = useCallback(() => {
    onChange('');
  }, [onChange]);

  return (
    <View
      style={[
        styles.wrap,
        {
          backgroundColor: theme.colors.surface.secondary,
          borderColor: theme.colors.border.secondary,
        },
      ]}
    >
      <Text style={[styles.glyph, { color: theme.colors.text.secondary }]}>⌕</Text>
      <TextInput
        value={value}
        onChangeText={onChange}
        placeholder={placeholder ?? callHistoryStrings.searchPlaceholder}
        placeholderTextColor={theme.colors.text.tertiary}
        style={[styles.input, { color: theme.colors.text.primary }]}
        accessibilityLabel={callHistoryStrings.searchPlaceholder}
        returnKeyType="search"
        autoCorrect={false}
        autoCapitalize="none"
      />
      {value.length > 0 ? (
        <Pressable
          onPress={handleClear}
          accessibilityRole="button"
          accessibilityLabel={callHistoryStrings.close}
          style={styles.clearWrap}
          hitSlop={8}
        >
          <Text style={[styles.clear, { color: theme.colors.text.secondary }]}>✕</Text>
        </Pressable>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    marginHorizontal: 16,
    marginVertical: 8,
    gap: 8,
  } satisfies ViewStyle,
  glyph: {
    fontSize: 16,
  } satisfies TextStyle,
  input: {
    flex: 1,
    fontSize: 14,
    paddingVertical: 0,
  } satisfies TextStyle,
  clearWrap: {
    paddingHorizontal: 4,
  } satisfies ViewStyle,
  clear: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
});
